// Playing area bookings, the MEMBER and PUBLIC side (BOOKINGS_PLAN.md §4):
// the grid, quotes, booking, my bookings, cancel and release, paying for a
// booking, and the visitor pages. Every call is one function here; the words
// and shapes live in utils/bookingsMember. Live updates (BE27): a signed-in
// grid joins the club's SignalR group and refetches on `bookingschanged`; a
// signed-out grid polls instead (there is no connection without a token).
import {onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {useSignalR} from '~/composables/useSignalR';
import {useStripePayments} from '~/composables/useStripePayments';
import {bookingsMemberEndpoints as ep} from '~/network/bookingsMemberEndpoints';

export {membershipApiError as bookingApiError} from '~/composables/useMembershipsAdmin';

export function useBookingsMember() {
  const {httpRequest} = useHttpRequest();
  const pending = ref(false);

  const call = async (endpoint, body) => {
    pending.value = true;
    try {
      return await httpRequest(endpoint, body ?? {});
    } finally {
      pending.value = false;
    }
  };

  // ── The grid ──
  const availability = (clubId, {from = null, days = null, sportId = null} = {}) =>
      httpRequest(ep.availability(clubId, {from, days, sportId}), null);
  const clubPage = (idOrSlug) => httpRequest(ep.clubPage(idOrSlug), null);

  // ── Booking (B5, B10, B11) ──
  const quote = (body) => httpRequest(ep.quote, body);
  const book = (body) => call(ep.create, body);
  const pay = (id, userPaymentMethodId = null) => call(ep.pay(id), {userPaymentMethodId});
  const paySync = (id) => call(ep.paySync(id));

  // ── Mine (B14, BE22) ──
  const mine = ({clubId = null, includePast = false} = {}) => httpRequest(ep.mine({clubId, includePast}), null);
  const get = (id) => httpRequest(ep.get(id), null);
  const cancel = (id, reason = null) => call(ep.cancel(id), {reason: reason || null});
  const release = (id) => call(ep.release(id));

  // ── Visitors (no login) ──
  const publicPage = (slug, returnUrl = null) => httpRequest(ep.publicPage(slug, returnUrl), null);
  const visitorStart = (slug, body) => call(ep.visitorStart(slug), body);
  const visitorGet = (token, returnUrl = null) => httpRequest(ep.visitorGet(token, returnUrl), null);
  const visitorConfirm = (token, code) => call(ep.visitorConfirm(token), {code});
  const visitorPay = (token, stripePaymentMethodId) => call(ep.visitorPay(token), {stripePaymentMethodId});
  const visitorPaySync = (token) => call(ep.visitorPaySync(token));
  const visitorCancel = (token) => call(ep.visitorCancel(token));

  /** Runs Stripe's next action (3-D Secure) for a booking charge on the club's connected account. */
  const {getStripe} = useStripePayments();
  const finishCardAction = async ({clientSecret, stripeAccountId}) => {
    const stripe = await getStripe(stripeAccountId || null);
    if (!stripe) return {error: {message: 'Secure payment could not start. Check your connection and try again.'}};
    return stripe.handleNextAction({clientSecret});
  };

  return {
    pending,
    availability, clubPage,
    quote, book, pay, paySync,
    mine, get, cancel, release,
    publicPage, visitorStart, visitorGet, visitorConfirm, visitorPay, visitorPaySync, visitorCancel,
    finishCardAction,
  };
}

/**
 * Live grid updates (B13, BE27). Signed in: join `ClubBookings_{clubId}` on
 * the hub while the grid is open and call `onChanged` on `bookingschanged`.
 * Signed out: poll every `pollMs`. Call from a component's setup; it leaves
 * the group and stops the timer when the component goes.
 *
 * Browser only (X63). Nothing starts until the component is mounted, and a
 * server render gets `refreshNow` and nothing else: the hub handler used to be
 * registered from setup, so every signed-in server render of /book left one in
 * useSignalR's module-level map, with the page's whole render tree behind it,
 * for the life of the process (~5.5 MB a render, MEMORY_M2).
 */
export function useLiveBookings(clubIdRef, {signedIn, onChanged, pollMs = 60000}) {
  if (import.meta.server) return {refreshNow: onChanged};

  const signalR = useSignalR();
  let joined = null;
  let timer = null;

  const handler = (payload) => {
    const id = payload?.clubId ?? payload?.ClubId ?? null;
    if (!id || !clubIdRef.value || String(id).toLowerCase() === String(clubIdRef.value).toLowerCase()) onChanged();
  };

  const join = async () => {
    if (!signedIn.value || !clubIdRef.value || !signalR.isConnected.value) return;
    if (joined === clubIdRef.value) return;
    try {
      await signalR.send('JoinClubBookings', clubIdRef.value);
      joined = clubIdRef.value;
    } catch (e) {
      console.warn('Could not join the club bookings group', e);
    }
  };
  const leave = async () => {
    if (!joined) return;
    const id = joined;
    joined = null;
    if (!signalR.isConnected.value) return;
    try { await signalR.send('LeaveClubBookings', id); } catch { /* the connection is gone */ }
  };

  const startPolling = () => {
    stopPolling();
    if (typeof window === 'undefined') return;
    timer = setInterval(() => { if (document.visibilityState !== 'hidden') onChanged(); }, pollMs);
  };
  const stopPolling = () => { if (timer) { clearInterval(timer); timer = null; } };

  const apply = () => {
    if (signedIn.value) { stopPolling(); signalR.on('bookingschanged', handler); join(); }
    else { leave(); signalR.off('bookingschanged', handler); startPolling(); }
  };

  // The watch is made inside onMounted (so it only ever runs in the browser);
  // Vue still ties it to the component and stops it on unmount.
  onMounted(() => {
    watch([signedIn, clubIdRef, signalR.isConnected], () => { leave().then(apply); }, {immediate: true});
  });

  onBeforeUnmount(() => {
    stopPolling();
    signalR.off('bookingschanged', handler);
    leave();
  });

  return {refreshNow: onChanged};
}
